import { Lock } from 'iconoir-react';
import React, { FunctionComponent, ReactElement, ReactNode } from 'react';

import { MessageType } from '@savgroup-front-common/types';

import { safeFormattedIntlString } from '../../../formatters';
import { SUPPORTED_ICON_POSITIONS } from '../../../helpers';
import { Icon } from '../Icon';
import LoadingBasedOnProps from '../LoadingBasedOnProps';

import { $InnerLinkStyled, $LockedTooltip } from './ButtonLink.styles';

export interface ButtonLinkProps {
  href?: string;
  icon?: ReactElement;
  isExternal?: boolean;
  target?: string;
  rel?: string;
  disabled?: boolean;
  isLoading?: boolean;
  children?: ReactNode;
  componentThemeName?: string;
  hollow?: boolean;
  underline?: boolean;
  secondary?: boolean;
  tertiary?: boolean;
  naked?: boolean;
  primary?: boolean;
  position?: SUPPORTED_ICON_POSITIONS;
  dataTestId?: string;
  download?: string;
  rounded?: boolean;
  success?: boolean;
  small?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  alert?: boolean;
  isFluid?: boolean;
  isMinimal?: boolean;
  ariaLabel?: string;
  isLocked?: boolean;
  lockedMessage?: MessageType;
}

const ButtonLink: FunctionComponent<
  React.PropsWithChildren<ButtonLinkProps>
> = ({
  href,
  icon,
  children,
  isExternal,
  target,
  rel,
  disabled = false,
  isLoading = false,
  hollow = false,
  underline = false,
  secondary = false,
  tertiary = false,
  naked = false,
  componentThemeName,
  primary = false,
  position = SUPPORTED_ICON_POSITIONS.LEFT,
  dataTestId,
  download,
  rounded = false,
  success = false,
  small = false,
  onClick,
  isFluid = false,
  isMinimal = false,
  ariaLabel,
  isLocked,
  lockedMessage,
  ...rest
}) => {
  const isSquareIconButton = Boolean(icon && children === undefined);

  return (
    <$InnerLinkStyled
      fluid={isFluid}
      isFluid={isFluid}
      rounded={rounded}
      href={href}
      isExternal={isExternal}
      target={target}
      rel={rel}
      $isSquareIconButton={isSquareIconButton}
      {...rest}
      disabled={disabled || isLocked}
      position={position}
      isLoading={isLoading}
      hollow={hollow}
      underline={underline}
      secondary={secondary}
      naked={naked}
      componentThemeName={componentThemeName}
      primary={primary}
      dataTestId={dataTestId}
      download={download}
      onClick={onClick}
      success={success}
      $isIcon={icon !== undefined}
      $isOnlyIcon={!!children && icon !== undefined}
      $isMinimal={isMinimal}
      ariaLabel={ariaLabel}
      tertiary={tertiary}
      small={small}
      haschildren={Boolean(children)}
    >
      <LoadingBasedOnProps
        disabled={disabled}
        isLoading={isLoading}
        secondary={secondary}
        primary={primary}
        tertiary={tertiary}
        success={success}
      >
        {!icon && !isLocked && children}
        {icon && !isLocked && (
          <Icon
            icon={icon}
            position={position}
            componentThemeName={componentThemeName}
            tertiary={tertiary}
            secondary={secondary}
          >
            {children}
          </Icon>
        )}
        {isLocked && (
          <$LockedTooltip content={safeFormattedIntlString(lockedMessage)}>
            <div>
              <Lock width={16} height={16} />
              {children}
            </div>
          </$LockedTooltip>
        )}
      </LoadingBasedOnProps>
    </$InnerLinkStyled>
  );
};

export default ButtonLink;
